import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, Container, Spinner, Table } from 'react-bootstrap';
import BinanceDepositPanel from './BinanceDepositPanel';
import SiteHeader from './SiteHeader';
import { useLanguage } from '../contexts/LanguageContext';
import './Landing.css';
import { t, tf } from '../utils/i18n';

const API_BASE = process.env.REACT_APP_API_URL || '';

function CheckoutFlowShell({ children, maxWidth = 720, centerContent = false }) {
  return (
    <div className="checkout-flow-page landing-theme-dark min-vh-100">
      <div className="checkout-flow-backdrop" aria-hidden="true" />
      <SiteHeader variant="app" />
      {centerContent ? (
        <div className="site-main-with-header min-vh-100 d-flex align-items-center justify-content-center">
          {children}
        </div>
      ) : (
        <Container className="site-main-with-header py-5" style={{ maxWidth }}>
          {children}
        </Container>
      )}
    </div>
  );
}

export default function ProductCheckout() {
  const { language } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const slugParam = (searchParams.get('slug') || '').trim();

  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [config, setConfig] = useState({ paypal: false, binanceDeposit: false });
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [payBusy, setPayBusy] = useState(false);
  const [payError, setPayError] = useState(null);
  const [binanceInfo, setBinanceInfo] = useState(null);
  const [success, setSuccess] = useState(null);
  const [pollTick, setPollTick] = useState(0);

  const servicesBase = language === 'en' ? '/services' : '/servicios';
  const productPageHref = slugParam ? `${servicesBase}/${slugParam}` : '/';

  useEffect(() => {
    let c = false;
    fetch(`${API_BASE}/auth/me`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!c) setUser(data);
      })
      .finally(() => {
        if (!c) setAuthLoading(false);
      });
    return () => {
      c = true;
    };
  }, []);

  useEffect(() => {
    fetch(`${API_BASE}/api/checkout/product/config`)
      .then((r) => (r.ok ? r.json() : {}))
      .then((data) => {
        setConfig({
          paypal: !!data.paypal,
          binanceDeposit: !!data.binanceDeposit
        });
      })
      .catch(() => {});
  }, []);

  const loadQuote = useCallback(async () => {
    if (!slugParam) return;
    setQuoteLoading(true);
    setQuoteError(null);
    setQuote(null);
    try {
      const r = await fetch(
        `${API_BASE}/api/checkout/product/quote?slug=${encodeURIComponent(slugParam)}`,
        { credentials: 'include' }
      );
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (data.error === 'product_not_found') {
          setQuoteError('checkoutProduct.errorProductNotFound');
        } else if (data.error === 'price_unavailable') {
          setQuoteError('checkoutProduct.errorPriceUnavailable');
        } else {
          setQuoteError('checkoutProduct.errorQuote');
        }
        return;
      }
      setQuote(data);
    } catch {
      setQuoteError('checkoutProduct.errorQuote');
    } finally {
      setQuoteLoading(false);
    }
  }, [slugParam]);

  useEffect(() => {
    loadQuote();
  }, [loadQuote]);

  useEffect(() => {
    if (searchParams.get('paypal_return') !== '1') return;
    const orderId = searchParams.get('token');
    if (!orderId || !user) return;
    let cancelled = false;
    setPayBusy(true);
    setPayError(null);
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/checkout/product/paypal/capture`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orderId })
        });
        const data = await r.json().catch(() => ({}));
        if (!cancelled && r.ok && data.status === 'captured') {
          setSuccess({ productName: data.productName, purchaseId: data.purchaseId });
          setSearchParams({ slug: data.slug || slugParam }, { replace: true });
        } else if (!cancelled) {
          setPayError('checkoutProduct.errorPayPalCapture');
        }
      } catch {
        if (!cancelled) setPayError('checkoutProduct.errorPayPalCapture');
      } finally {
        if (!cancelled) setPayBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams, user, slugParam, setSearchParams]);

  const startPayPal = async () => {
    if (!quote || !user) return;
    setPayBusy(true);
    setPayError(null);
    try {
      const r = await fetch(`${API_BASE}/api/checkout/product/create-order`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug: quote.slug, provider: 'paypal' })
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data.approvalUrl) {
        setPayError('checkoutProduct.errorPayPalStart');
        setPayBusy(false);
        return;
      }
      window.location.href = data.approvalUrl;
    } catch {
      setPayError('checkoutProduct.errorPayPalStart');
      setPayBusy(false);
    }
  };

  const startBinance = async () => {
    if (!quote || !user) return;
    setPayBusy(true);
    setPayError(null);
    setBinanceInfo(null);
    try {
      const r = await fetch(`${API_BASE}/api/checkout/product/create-order`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug: quote.slug, provider: 'binance_deposit' })
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setPayError('checkoutProduct.errorBinanceStart');
        return;
      }
      setBinanceInfo(data);
    } catch {
      setPayError('checkoutProduct.errorBinanceStart');
    } finally {
      setPayBusy(false);
    }
  };

  useEffect(() => {
    if (!binanceInfo?.reference || !user) return;
    const id = setInterval(() => setPollTick((n) => n + 1), 12000);
    return () => clearInterval(id);
  }, [binanceInfo?.reference, user]);

  useEffect(() => {
    if (!binanceInfo?.reference || !user || success) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${API_BASE}/api/checkout/product/status?reference=${encodeURIComponent(binanceInfo.reference)}`,
          { credentials: 'include' }
        );
        const data = await r.json().catch(() => ({}));
        if (!cancelled && r.ok && data.status === 'captured') {
          setSuccess({ productName: data.productName, purchaseId: data.purchaseId });
          setBinanceInfo(null);
        }
      } catch {
        /* poll again */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [binanceInfo?.reference, user, success, pollTick]);

  if (authLoading) {
    return (
      <CheckoutFlowShell centerContent>
        <Spinner animation="border" variant="light" />
      </CheckoutFlowShell>
    );
  }

  if (!user) {
    const ret = `/checkout/product?slug=${encodeURIComponent(slugParam)}`;
    return (
      <CheckoutFlowShell maxWidth={560}>
          <Card className="checkout-shell-card shadow-none">
            <Card.Body className="p-4">
              <h1 className="h4 mb-3 text-white">{t('checkoutProduct.loginRequiredTitle', language)}</h1>
              <p className="checkout-flow-lead mb-4">{t('checkoutProduct.loginRequiredBody', language)}</p>
              <Button as={Link} to={`/user?return=${encodeURIComponent(ret)}`} className="landing-btn-primary border-0">
                {t('checkoutProduct.goToLogin', language)}
              </Button>
            </Card.Body>
          </Card>
      </CheckoutFlowShell>
    );
  }

  if (!slugParam) {
    return (
      <CheckoutFlowShell>
        <Alert variant="warning" className="border-0">
          {t('checkoutProduct.missingSlug', language)}
        </Alert>
        <Link to="/" className="landing-news-all-link d-inline-flex mt-2">
          {t('checkoutProduct.backHome', language)}
        </Link>
      </CheckoutFlowShell>
    );
  }

  if (success) {
    return (
      <CheckoutFlowShell maxWidth={640}>
        <Alert variant="success" className="border-0">
          <strong>{t('checkoutProduct.successTitle', language)}</strong>
          <p className="mb-0 mt-2 small">
            {tf('checkoutProduct.successBody', { productName: success.productName }, language)}
          </p>
        </Alert>
        <p className="checkout-flow-lead">{t('checkoutProduct.fulfillmentNote', language)}</p>
        <Button as={Link} to="/user" className="landing-btn-primary border-0">
          {t('checkoutProduct.goToAccount', language)}
        </Button>
      </CheckoutFlowShell>
    );
  }

  return (
    <CheckoutFlowShell maxWidth={720}>
        <div className="mb-4 d-flex flex-wrap gap-3">
          <Link to="/" className="landing-news-all-link d-inline-flex">
            {t('checkoutProduct.backHome', language)}
          </Link>
          <Link to={productPageHref} className="landing-news-all-link d-inline-flex">
            {t('checkoutProduct.backProduct', language)}
          </Link>
        </div>
        <h1 className="h3 mb-3 text-white">{t('checkoutProduct.title', language)}</h1>
        <p className="checkout-flow-lead">{t('checkoutProduct.intro', language)}</p>

        {searchParams.get('cancelled') === '1' && (
          <Alert variant="secondary" className="small border-0">
            {t('checkoutProduct.payPalCancelled', language)}
          </Alert>
        )}

        {quoteLoading && (
          <div className="text-center py-4 checkout-flow-loading">
            <Spinner animation="border" size="sm" className="me-2" />
            {t('checkoutProduct.loadingQuote', language)}
          </div>
        )}

        {quoteError && <Alert variant="danger" className="border-0">{t(quoteError, language)}</Alert>}
        {payError && <Alert variant="danger" className="border-0">{t(payError, language)}</Alert>}

        {quote && !quoteLoading && (
          <Card className="checkout-shell-card shadow-none mb-4">
            <Card.Body>
              <Table borderless size="sm" className="mb-0 checkout-flow-quote-table">
                <tbody>
                  <tr>
                    <td className="checkout-flow-label">{t('checkoutProduct.labelProduct', language)}</td>
                    <td className="checkout-flow-value">{quote.name}</td>
                  </tr>
                  <tr>
                    <td className="checkout-flow-label">{t('checkoutProduct.labelAmount', language)}</td>
                    <td className="checkout-flow-value checkout-flow-value-total">
                      {tf('checkoutProduct.amountUsd', { amount: String(quote.amountUsd) }, language)}
                    </td>
                  </tr>
                </tbody>
              </Table>
              <p className="small checkout-flow-disclaimer mt-3 mb-0">{t('checkoutProduct.quoteDisclaimer', language)}</p>
            </Card.Body>
          </Card>
        )}

        {quote && !quoteLoading && (
          <div className="d-flex flex-column gap-3">
            {config.paypal && (
              <Button className="landing-btn-primary border-0" size="lg" disabled={payBusy} onClick={startPayPal}>
                {payBusy ? <Spinner size="sm" animation="border" className="me-2" /> : null}
                {t('checkoutProduct.payWithPayPal', language)}
              </Button>
            )}
            {config.binanceDeposit && (
              <Button variant="outline-light" size="lg" disabled={payBusy} onClick={startBinance}>
                {t('checkoutProduct.payWithBinanceWallet', language)}
              </Button>
            )}
            {!config.paypal && !config.binanceDeposit && (
              <Alert variant="warning" className="border-0">{t('checkoutProduct.noGateways', language)}</Alert>
            )}
          </div>
        )}

        {binanceInfo && (
          <Card className="mt-4 checkout-shell-card border-warning shadow-none">
            <Card.Body>
              <BinanceDepositPanel
                info={binanceInfo}
                language={language}
                translationPrefix="checkoutProduct"
                disclaimerClassName="checkout-flow-disclaimer"
                titleClassName="h6 text-white"
              />
            </Card.Body>
          </Card>
        )}
    </CheckoutFlowShell>
  );
}
